import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { useToast } from '~/shared/contexts/ToastContext';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { User } from '~/shared/types/connect';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  expertId: string;
  expert: User;
  mattersWithoutExpert: MatterViewModel[];
}

const AddToMatterModal = ({
  isOpen,
  onClose,
  expertId,
  expert,
  mattersWithoutExpert,
}: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { addExpertToMatter, isAddingExpert } = useMatterMutations();
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const handleClose = () => {
    setSelectedMatterId(null);
    setAddError(null);
    onClose();
  };

  const handleAdd = async () => {
    if (!selectedMatterId) {
      return;
    }
    try {
      setAddError(null);
      await addExpertToMatter(selectedMatterId, expertId);
      showToast('Expert added to matter');
      handleClose();
      navigate(`/matters/${selectedMatterId}`);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'add_expert_to_matter' },
        extra: { matterId: selectedMatterId, expertId },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to add expert. Please try again.';
      setAddError(message);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <ModalHeader
        title="Add to Matter"
        subtitle={
          <>
            Select a matter to add{' '}
            <strong>{expert.name ?? 'this expert'}</strong> to.
          </>
        }
        onClose={handleClose}
      />
      <StyledDialogContent>
        {mattersWithoutExpert.length === 0 ? (
          <EmptyBox>
            <Typography color="text.secondary">
              No matters available. All your matters already have this expert
              assigned or added. Create a new matter first.
            </Typography>
          </EmptyBox>
        ) : (
          <StyledList disablePadding>
            {mattersWithoutExpert.map((matter) => (
              <StyledListItemButton
                key={matter.id}
                selected={selectedMatterId === matter.id}
                onClick={() =>
                  setSelectedMatterId(
                    selectedMatterId === matter.id ? null : matter.id,
                  )
                }
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedMatterId === matter.id}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={matter.title}
                  secondary={
                    matter.caseNumber ?? matter.caseDescription.slice(0, 60)
                  }
                />
              </StyledListItemButton>
            ))}
          </StyledList>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        {addError && (
          <ErrorText variant="body2" color="error">
            {addError}
          </ErrorText>
        )}
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={!selectedMatterId || isAddingExpert}
        >
          {isAddingExpert ? 'Adding…' : 'Add to Matter'}
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
};

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const EmptyBox = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  textAlign: 'center',
}));

const StyledList = styled(List)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

export default AddToMatterModal;
